import { createContext, type PropsWithChildren, useMemo } from "react";

import { useNonNullableContext } from "app/common";

import type { IFiltersConfigCategoryItem } from "../types/category";
import type { FiltersConfig } from "../types/filtersConfig";
import type { SearchFilterInstance } from "../types/searchFilterInstance";

type FiltersConfigContextValue<T extends SearchFilterInstance = SearchFilterInstance, TKey extends string = string> = FiltersConfig<readonly T[], TKey> & {
    readonly categoriesList: readonly IFiltersConfigCategoryItem[];
};

type FiltersConfigProviderProps<T extends SearchFilterInstance = SearchFilterInstance, TKey extends string = string> = PropsWithChildren<
    FiltersConfigContextValue<T, TKey>
>;

const FiltersConfigContext = createContext<FiltersConfigContextValue | null>(null);

export const useFiltersConfigContext = <
    T extends SearchFilterInstance = SearchFilterInstance,
    TKey extends string = string,
>(): FiltersConfigContextValue<T, TKey> => useNonNullableContext(FiltersConfigContext) as FiltersConfigContextValue<T, TKey>;

export const FiltersConfigProvider = <T extends SearchFilterInstance = SearchFilterInstance, TKey extends string = string>({
    children,
    filtersList,
    status,
    filtersSearchConfigList,
    categoriesList,
}: FiltersConfigProviderProps<T, TKey>) => {
    const value = useMemo(
        () => ({ filtersList, status, filtersSearchConfigList, categoriesList }),
        [filtersList, status, filtersSearchConfigList, categoriesList],
    );

    return <FiltersConfigContext.Provider value={value}>{children}</FiltersConfigContext.Provider>;
};
